import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  globalStyle,
  handleVisible,
  handleDangerVisible,
  formatId,
  SECTION_236H_FIELDS,
  sanitizeDecimalInput,
} from "../../global/Utils";
import {
  addCompany,
  getProvince,
  uploadDocument,
} from "../../Actions/CompanyAction";
import { getUserList } from "../../Actions/UserAction";
import { getTransaction } from "../../Actions/InvoicesAction";
import { InputField, MobileNumberField } from "../Common/InputField";
import { CircularProgress, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ScenarioGridMultiSelect } from "../Common/ScenarioGridSelect";

import { upperCaseInput } from "../../global/textCase";
import { SearchableSelect } from "../Common/SearchableSelect";
import { YES_NO_OPTIONS } from "../../global/itemDisplay";
import {
  LANDLINE_ERROR,
  landlineToSave,
  landlineTypingError,
  typedLandline,
} from "../../global/landline";
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const AddCompany = () => {
  const dispatch = useDispatch();
  const [idType, setIdType] = useState("NTN");
  const formRef = React.useRef(null);

  const { isAddingCompanyList, isUploadingDocument, provinceList } =
    useSelector((state) => state.CompanyListReducer);

  const { userList, isGettingUserList } = useSelector(
    (state) => state.UserReducer,
  );

  const { transactionType, isGettingTransactionType } = useSelector(
    (state) => state.InvoicesListReducer,
  );

  const { userDetails } = useSelector((state) => state?.HeaderReducer);

  const [formData, setFormData] = useState({
    companyName: "",
    CEOName: "",
    CEOContact: "",
    CEOLandline: "",
    CEOEmail: "",
    website: "",
    ntn: "",
    cnic: "",
    salesTaxRegNo: "",
    posId: "",
    registrationType: "",
    businessAddress: "",
    province: "",
    GSection236HRegister: "",
    GSection236HNonRegister: "",
    logo: null,
    invoicePattern: "Default",
    concatSerialWithProductName: "No",
    furtherTaxApplicable: "No",
    termsHeading: "",
    termsText: "",
    sandBoxToken: "",
    productiontoken: "",
    userId: "",
    assignedScenarios: [],
  });

  const [errors, setErrors] = useState({});
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");

  /* ------------------ HANDLE INPUT CHANGE ------------------ */
  const handleChange = (e) => {
    const { name, value } = upperCaseInput(e.target);
    if (name === "sandBoxToken" || name === "productiontoken") {
      if (value.length > 36) {
        setErrors((prev) => ({
          ...prev,
          [name]: "Token must be 36 characters long",
        }));
        return;
      }
    }
    if (name === "CEOContact") {
      let numericValue = value.replace(/\D/g, "");
      numericValue = numericValue.slice(0, 10);
      let formatted = numericValue;
      if (numericValue.length > 3) {
        formatted = `${numericValue.slice(0, 3)}-${numericValue.slice(3)}`;
      }
      if (numericValue && numericValue[0] !== "3") {
        setErrors((prev) => ({
          ...prev,
          [name]: "Pakistani number must start with 3",
        }));
      } else {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
      setFormData((prev) => ({
        ...prev,
        [name]: formatted,
      }));
      return;
    }
    if (name === "CEOLandline") {
      const typed = typedLandline(value);
      setFormData((prev) => ({ ...prev, [name]: typed }));
      setErrors((prev) => ({ ...prev, [name]: landlineTypingError(typed) }));
      return;
    }
    if (name === "ntn") {
      let processedValue;
      if (idType === "NTN") {
        const cleaned = value.replace(/[^a-zA-Z0-9]/g, "");
        const first = cleaned.slice(0, 1);
        const rest = cleaned.slice(1).replace(/[^0-9]/g, "");
        processedValue = (first + rest).slice(0, 7);
      } else {
        processedValue = value.replace(/\D/g, "").slice(0, 13);
      }
      setErrors((prev) => ({ ...prev, [name]: "" }));
      setFormData((prev) => ({ ...prev, [name]: processedValue }));
      return;
    }
    if (name === "posId") {
      const processedValue = value.replace(/\D/g, "");
      setFormData((prev) => ({ ...prev, [name]: processedValue }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }
    if (name === "cnic") {
      const processedValue = value.replace(/\D/g, "").slice(0, 14);
      setFormData((prev) => ({ ...prev, [name]: processedValue }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }
    if (SECTION_236H_FIELDS.includes(name)) {
      setFormData((prev) => ({ ...prev, [name]: sanitizeDecimalInput(value) }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  /* ------------------ HANDLE FILE CHANGE ------------------ */
  const handleFileChange = (file) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setFileError("File size must be less than 5 MB");
      return;
    }
    setFileError("");
    dispatch(
      uploadDocument(
        { image: file },
        handleDangerVisible,
        setFormData,
        setFile,
      ),
    );
  };

  /* ------------------ VALIDATION ------------------ */
  const validate = () => {
    const newErrors = {};

    const requiredFields = [
      "companyName",
      "CEOName",
      "CEOContact",
      "ntn",
      "registrationType",
      "province",
      "sandBoxToken",
      "assignedScenarios",
      "businessAddress",
      userDetails?.role === "admin" && "userId",
    ].filter(Boolean);

    requiredFields.forEach((field) => {
      const value = formData[field];
      if (Array.isArray(value)) {
        if (value.length === 0) {
          newErrors[field] = "This field is required";
        }
      } else if (!value || !value.toString().trim()) {
        newErrors[field] = "This field is required";
      }
    });
    if (formData.CEOContact) {
      const numericValue = formData.CEOContact.replace(/\D/g, "");
      if (numericValue.length !== 10) {
        newErrors.CEOContact = "Contact number must be 10 digits";
      } else if (!numericValue.startsWith("3")) {
        newErrors.CEOContact = "Pakistani number must start with 3";
      }
    }
    if (landlineToSave(formData.CEOLandline) === null) {
      newErrors.CEOLandline = LANDLINE_ERROR;
    }
    if (formData.posId && !(Number(formData.posId) > 0)) {
      newErrors.posId = "POS ID must be greater than 0";
    }
    if (formData.ntn && idType === "NTN") {
      if (!/^([A-Za-z]\d{6}|\d{7})$/.test(formData.ntn)) {
        newErrors.ntn = "NTN must be 7 digits, or 1 letter followed by 6 digits";
      }
    }
    SECTION_236H_FIELDS.forEach((field) => {
      if (Number(formData[field]) > 100) {
        newErrors[field] = "Percentage cannot be more than 100";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ------------------ SUBMIT ------------------ */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      setTimeout(() => {
        const firstError = document.querySelector(".border-red-500");
        if (firstError) {
          firstError.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          firstError.focus();
        }
      }, 100);
      return;
    }

    const submitData = {
      ...formData,
      CEOContact: "92" + formData.CEOContact.replace(/\D/g, ""),
      CEOLandline: landlineToSave(formData.CEOLandline),
      GSection236HRegister: Number(formData.GSection236HRegister) || 0,
      GSection236HNonRegister: Number(formData.GSection236HNonRegister) || 0,
    };

    dispatch(
      addCompany(
        submitData,
        setFormData,
        setFile,
        handleVisible,
        handleDangerVisible,
      ),
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getProvince());
      dispatch(getUserList("", "", "", ""));
      dispatch(getTransaction());
    }, 500);
    return () => clearTimeout(timer);
  }, [dispatch]);

  const handleRemoveFile = () => {
    setFormData((prev) => ({
      ...prev,
      logo: "",
    }));
    setFileError("");
    setFile(null);
  };

  return (
    <div className="w-full min-h-screen flex justify-center py-4">
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="w-full space-y-6"
      >
        <h2
          className="text-center font-bold text-xl"
          style={{
            fontFamily: globalStyle.fontFamilyHeading,
            color: globalStyle.curveDarkColor,
          }}
        >
          Add Company Information
        </h2>

        {/* GRID */}
        {userDetails?.role === "admin" &&
          (isGettingUserList ? (
            <div className="flex justify-center items-center h-20">
              <CircularProgress />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <label className="block mb-1 font-medium">
                Select User<span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                name="userId"
                value={formData?.userId}
                onChange={handleChange}
                disabled={!userList || userList.length === 0}
                className={`w-full px-4 py-2 border rounded-md ${
                  errors.userId ? "border-red-500" : "border-gray-300"
                }`}
              >
                {!userList || userList.length === 0 ? (
                  <option value="">No Users Available</option>
                ) : (
                  <>
                    <option value="">SELECT</option>
                    {userList.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.email}
                      </option>
                    ))}
                  </>
                )}
              </SearchableSelect>
              {errors.userId && (
                <p className="text-red-500 text-sm mt-1">{errors.userId}</p>
              )}
            </div>
          ))}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            label="Company Name"
            name="companyName"
            value={formData.companyName}
            error={errors.companyName}
            onChange={handleChange}
            required
          />
          <InputField
            label="CEO Name"
            name="CEOName"
            value={formData.CEOName}
            error={errors.CEOName}
            onChange={handleChange}
            required
          />
          <MobileNumberField
            label="CEO Contact"
            name="CEOContact"
            value={formData.CEOContact}
            error={errors.CEOContact}
            onChange={handleChange}
            required
          />
          <InputField
            label="Company Landline"
            name="CEOLandline"
            value={formData.CEOLandline}
            error={errors.CEOLandline}
            onChange={handleChange}
            placeholder="XXX-XXXXXXXX"
          />
          <InputField
            label="Bussiness Address"
            name="businessAddress"
            value={formData.businessAddress}
            error={errors.businessAddress}
            onChange={handleChange}
            required
          />
          <div className="w-full">
            <div className="flex gap-6 mb-2">
              <label
                htmlFor="ntn"
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name="idType"
                  value="NTN"
                  checked={idType === "NTN"}
                  onChange={() => setIdType("NTN")}
                />
                NTN{" "}
                {idType === "NTN" ? (
                  <span className="text-red-500">*</span>
                ) : (
                  " "
                )}
              </label>
              <label
                htmlFor="cnic"
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name="idType"
                  value="CNIC"
                  checked={idType === "CNIC"}
                  onChange={() => setIdType("CNIC")}
                />
                CNIC{" "}
                {idType === "CNIC" ? (
                  <span className="text-red-500">*</span>
                ) : (
                  " "
                )}
              </label>
            </div>
            <input
              type="text"
              name="ntn"
              value={formatId(formData.ntn, idType)}
              onChange={handleChange}
              placeholder={idType === "NTN" ? "XXXXXXX" : "XXXXX-XXXXXXX-X"}
              className={`w-full px-4 py-2 border rounded-md focus:outline-none ${
                errors.ntn
                  ? "border-red-500"
                  : "border-gray-300 focus:border-blue-500"
              }`}
            />

            {errors.ntn && (
              <p className="text-red-500 text-sm mt-1">{errors.ntn}</p>
            )}
          </div>
          {/* Registration Type */}
          <div>
            <label className="block mb-1 font-medium">
              Registration Type <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              name="registrationType"
              value={formData.registrationType}
              onChange={handleChange}
              className={`uppercase w-full px-4 py-2 border rounded-md ${
                errors.registrationType ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select</option>
              <option value="Registered">Registered</option>
              <option value="UnRegistered">UnRegistered</option>
            </SearchableSelect>
            {errors.registrationType && (
              <p className="text-red-500 text-sm mt-1">
                {errors.registrationType}
              </p>
            )}
          </div>
          <div>
            <label className="block mb-1 font-medium">
              Province<span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              name="province"
              value={formData.province}
              onChange={handleChange}
              disabled={!provinceList || provinceList.length === 0}
              className={`uppercase w-full px-4 py-2 border rounded-md ${
                errors.province ? "border-red-500" : "border-gray-300"
              }`}
            >
              {!provinceList || provinceList.length === 0 ? (
                <option value="">No Province Available</option>
              ) : (
                <>
                  <option value="">Select</option>
                  {provinceList.map((value) => (
                    <option
                      key={value.stateProvinceCode}
                      value={value.stateProvinceDesc}
                    >
                      {value.stateProvinceDesc}
                    </option>
                  ))}
                </>
              )}
            </SearchableSelect>
            {errors.province && (
              <p className="text-red-500 text-sm mt-1">{errors.province}</p>
            )}
          </div>
          <div>
            <label className="block mb-1 font-medium">
              Assigned Scenarios <span className="text-red-500">*</span>
            </label>
            {isGettingTransactionType ? (
              <div className="flex justify-center items-center h-20">
                <CircularProgress />
              </div>
            ) : (
              <ScenarioGridMultiSelect
                name="assignedScenarios"
                scenarios={transactionType}
                value={formData.assignedScenarios}
                disabled={!transactionType || transactionType.length === 0}
                className={`w-full px-3 py-1.5 min-h-[42px] border rounded-md ${errors.assignedScenarios ? "border-red-500" : "border-gray-300"}`}
                onChange={(e = []) => {
                  setFormData((pre) => ({
                    ...pre,
                    assignedScenarios: e.map(
                      (scenario) => scenario?.value || "",
                    ),
                  }));
                  setErrors((prev) => ({ ...prev, assignedScenarios: "" }));
                }}
              />
            )}
            {errors.assignedScenarios && (
              <p className="text-red-500 text-sm mt-1">
                {errors.assignedScenarios}
              </p>
            )}
          </div>

          <InputField
            label="Website"
            name="website"
            value={formData.website}
            error={errors.website}
            onChange={handleChange}
          />
          <InputField
            label="CEO CNIC"
            name="cnic"
            type="number"
            value={formData.cnic}
            error={errors.cnic}
            onChange={handleChange}
          />
          <InputField
            label="POS ID"
            name="posId"
            value={formData.posId}
            error={errors.posId}
            onChange={handleChange}
          />
          <InputField
            label="Sale Tax No. (STRN)"
            name="salesTaxRegNo"
            value={formData.salesTaxRegNo}
            error={errors.salesTaxRegNo}
            onChange={handleChange}
          />
          <InputField
            label="Register GSection 236H %"
            name="GSection236HRegister"
            value={formData.GSection236HRegister}
            error={errors.GSection236HRegister}
            onChange={handleChange}
          />
          <InputField
            label="Non-Register GSection 236H %"
            name="GSection236HNonRegister"
            value={formData.GSection236HNonRegister}
            error={errors.GSection236HNonRegister}
            onChange={handleChange}
          />
          <InputField
            label="Terms Heading"
            name="termsHeading"
            value={formData.termsHeading}
            error={errors.termsHeading}
            onChange={handleChange}
          />

          <InputField
            label="Terms Text"
            name="termsText"
            value={formData.termsText}
            error={errors.termsText}
            onChange={handleChange}
          />

          {/* LOGO */}
          <div>
            <label className="block mb-1 font-medium">Company Logo</label>
            {isUploadingDocument ? (
              <div className="flex justify-center items-center h-20">
                <CircularProgress />
              </div>
            ) : !file ? (
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e.target.files[0])}
                className="w-full"
              />
            ) : (
              <div className="flex items-center justify-between border rounded-md px-3 py-2 bg-gray-50">
                <span className="text-sm truncate">{file.name}</span>
                <IconButton size="small" onClick={handleRemoveFile}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            )}
            {fileError && (
              <p className="text-red-500 text-sm mt-1">{fileError}</p>
            )}
          </div>

          {/* INVOICE PATTERN */}
          <div>
            <label className="block mb-1 font-medium">Invoice Pattern</label>
            <SearchableSelect
              name="invoicePattern"
              value={formData.invoicePattern}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-md ${
                errors.invoicePattern ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="Default">Default</option>
              <option value="With Company Logo">With Company Logo</option>
              <option value="Logo Only">Logo Only</option>
              <option value="Thermal Invoice">Thermal Invoice</option>
              <option value="Half Page Invoice">Half Page Invoice</option>
            </SearchableSelect>
            {errors.invoicePattern && (
              <p className="text-red-500 text-sm mt-1">
                {errors.invoicePattern}
              </p>
            )}
          </div>

          {/* CONCAT SERIAL WITH PRODUCT NAME */}
          <div>
            <label className="block mb-1 font-medium">
              Concat serial number with Product Name
            </label>
            <SearchableSelect
              name="concatSerialWithProductName"
              value={formData.concatSerialWithProductName}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-md border-gray-300"
            >
              {YES_NO_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </SearchableSelect>
          </div>
          {/* FURTHER TAX APPLICABLE */}
          <div>
            <label className="block mb-1 font-medium">
              Further Tax Applicable
            </label>
            <SearchableSelect
              name="furtherTaxApplicable"
              value={formData.furtherTaxApplicable}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-md border-gray-300"
            >
              {YES_NO_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </SearchableSelect>
            <p className="text-gray-500 text-xs mt-1">
              Yes: every item of this company needs a Further Tax % (starts at
              4%).
            </p>
          </div>
        </div>

        {/* TOKENS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            label="Sandbox Token"
            name="sandBoxToken"
            value={formData.sandBoxToken}
            error={errors.sandBoxToken}
            onChange={handleChange}
            required
          />
          <InputField
            label="Production Token"
            name="productiontoken"
            value={formData.productiontoken}
            error={errors.productiontoken}
            onChange={handleChange}
          />
        </div>

        {/* SUBMIT */}
        <button
          type="submit"
          disabled={isAddingCompanyList}
          className="w-full md:w-1/3 mx-auto block py-2 rounded-md text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
          style={{ background: globalStyle.buttonColor }}
        >
          {isAddingCompanyList ? "Please Wait..." : "Submit"}
        </button>
      </form>
    </div>
  );
};

export default AddCompany;
